/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TraceTrack.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/* global globalThis */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import TraceTrackCards from "./TraceTrackCards";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

const getCloseNavigationPath = (page, tabFromUrl) => {
  if (page === "intersections") {
    return `/dashboard/v2/type-approval/intersection?tab=${tabFromUrl}`;
  }
  if (page === "simulations") {
    return `/dashboard/doc_library/stimulation?tab=${tabFromUrl}`;
  }
  return `/dashboard/v2/type-approval/tickets?tab=${tabFromUrl}`;
};

const getTraceComponentDescription = (traceTrack) => {
  if (traceTrack?.swm_details?.swm_id) {
    return `Software Module - Number: ${traceTrack?.swm_details?.name}`;
  }
  if (traceTrack?.ee_details?.ee_id) {
    return `EE Component - Assembly No: ${traceTrack?.ee_details?.assembly_number}`;
  }
  if (traceTrack?.vehicle_component_details?.name) {
    return `Vehicle Component - ${traceTrack?.vehicle_component_details?.name}`;
  }
  if (traceTrack?.vehicle_system_details?.name) {
    return `Vehicle System - ${traceTrack?.vehicle_system_details?.name}`;
  }
  return "-";
};

const filterTraceTrackData = (traceData, intersectionCreatedAtStr) => {
  const intersectionCreatedAt = new Date(intersectionCreatedAtStr);
  return (traceData || []).filter((traceTrack) => {
    const createdAt = new Date(traceTrack?.created_at);
    return createdAt <= intersectionCreatedAt;
  });
};

function TraceTrack() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileDetails = useSelector((store) => store.profileReducer);
  const urlParams = new URLSearchParams(globalThis.location.search);
  const intrID = urlParams.get("intr_id");
  const project_id = urlParams.get("project_id");
  const variant_id = urlParams.get("variant_id");
  const parentPage = urlParams.get("parent_page");
  const tabFromUrl = urlParams.get("tab");
  const [trackTraceDetailsList, setTrackTraceDetailsList] = useState([]);
  const [selectedTrackTraceDetails, setSelectedTrackTraceDetails] =
    useState(null);
  const [selectedTicketsTableRowData, setSelectedTicketsTableRowData] =
    useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetchTickets = (requestObj = {}) => {
      dispatch(
        HandleApiActions({
          ...typeApprovalAPI?.get_tickets,
          params: {
            project_id: Number(project_id),
            variant_id: Number(variant_id),
            intr_id: Number(intrID),
            ...requestObj,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        console.log("---fetchTickets---", resp);
        if (resp?.error) {
          setSelectedTicketsTableRowData(null);
        } else {
          setSelectedTicketsTableRowData(resp?.data?.[0] || null);
        }
      });
    };

    console.log("---intrID ticket call---", intrID);
    if (intrID) fetchTickets();
  }, [intrID, dispatch, project_id, variant_id]);

  useEffect(() => {
    const handleGetTrackTraceDetails = (intr_id) => {
      console.log("---handleGetTrackTraceDetails---", intr_id);
      dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets_trace_track_details,
          params: {
            intr_id: Number(intr_id),
            order_column: "created_at",
            order_sort: "DESC",
            page_number: 1,
            page_size: 99,
          },
        }),
      ).then((resp) => {
        if (!resp?.error) {
          const traceTraceData = resp?.data || [];
          const filteredTraceTraceData = filterTraceTrackData(
            traceTraceData,
            selectedTicketsTableRowData?.created_at,
          );
          setTrackTraceDetailsList(filteredTraceTraceData);
          setSelectedTrackTraceDetails(filteredTraceTraceData?.[0]);
        }
      });
    };

    console.log(
      "---selectedTicketsTableRowData for handleGetTrackTraceDetails---",
      selectedTicketsTableRowData,
    );
    if (selectedTicketsTableRowData) {
      const intr_id = selectedTicketsTableRowData?.intr_id;
      handleGetTrackTraceDetails(intr_id);
    }
  }, [selectedTicketsTableRowData, dispatch]);

  return (
    <Box sx={{ width: "100%", p: 2 }} key={intrID}>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <CommonBreadcrumbs
              items={[
                { label: "Regulatory Compliance" },
                { label: "Tickets" },
                { label: "Trace" },
              ]}
            />
            <IconButton
              onClick={() => {
                navigate(getCloseNavigationPath(parentPage, tabFromUrl));
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Grid>

        <Grid item sm={12}>
          {trackTraceDetailsList.length > 0 ? (
            trackTraceDetailsList?.map((traceTrack, index) => {
              const itemKey = traceTrack?.intr_id || `trace-${index}`;
              const formattedDate = convertUtcToTimeZone(
                traceTrack?.created_at,
                profileDetails?.profile?.profile?.region,
              )?.slice(0, 5);
              const componentDesc = getTraceComponentDescription(traceTrack);

              return (
                <Accordion
                  key={itemKey}
                  expanded={expanded === index}
                  sx={{
                    boxShadow: 3,
                    borderRadius: 0,
                    "&:before": { display: "none" },
                    p: 0,
                    "& .MuiAccordionDetails-root": {
                      p: 0,
                    },
                  }}
                  onChange={() => {
                    setSelectedTrackTraceDetails(trackTraceDetailsList[index]);
                    setExpanded(expanded === index ? null : index);
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}
                    sx={{
                      backgroundColor: "black",
                      color: "white",
                      minHeight: "48px !important",
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        p: 0,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      {`${formattedDate} Intersection Id ${traceTrack?.intr_id} From ${componentDesc}`}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "white",
                      height: "430px",
                    }}
                  >
                    <TraceTrackCards
                      selectedTrackTraceDetails={selectedTrackTraceDetails}
                      projectID={project_id}
                      variantID={variant_id}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <Typography>No Data Found</Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default TraceTrack;
